import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { TEAM } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus2, Trash2, Copy, ShieldCheck, Crown, Sparkles, Key, Clock, Mail, Receipt, Megaphone, History, CalendarDays, Check } from "lucide-react";
import { toast } from "sonner";

const ROLE_TONE = {
  owner: "bg-slate-900 text-white",
  admin: "bg-teal-50 text-teal-800 border border-teal-100",
  cleaner: "bg-amber-50 text-amber-800 border border-amber-100",
};

const ROLE_DESC = {
  owner: "Full access · can manage team, billing and all data",
  admin: "Everyday CRM access · guests, contractors, bookings, campaigns",
  cleaner:
    "Turnover crew access · Dashboard, Calendar, Cleaning and read-only Properties (door codes, Wi-Fi, bedding)",
};

const ROLE_LABEL = {
  owner: "Owner",
  admin: "Administrator",
  cleaner: "Cleaner",
};

// Session Z85 — distinctive illustration per role so the gallery feels crafted.
// Each role gets its own gradient, headline icon, accent colour and curated
// capability chip list.
const ROLE_THEME = {
  owner: {
    gradient: "from-slate-900 via-slate-800 to-teal-900",
    text: "text-white",
    sub: "text-teal-200/80",
    bodyText: "text-slate-200",
    chipBg: "bg-white/10 text-white border border-white/15",
    accent: "text-teal-300",
    Icon: Crown,
    tagline: "Captain of the ship",
    capabilities: [
      { icon: Key, label: "Billing & keys" },
      { icon: ShieldCheck, label: "Manage team" },
      { icon: Megaphone, label: "All data" },
    ],
  },
  admin: {
    gradient: "from-teal-50 via-white to-teal-50",
    text: "text-slate-900",
    sub: "text-teal-700",
    bodyText: "text-slate-600",
    chipBg: "bg-white border border-teal-100 text-teal-800",
    accent: "text-teal-700",
    Icon: ShieldCheck,
    tagline: "Everyday operations",
    capabilities: [
      { icon: Mail, label: "Inbox & templates" },
      { icon: Receipt, label: "Bookings & costs" },
      { icon: Megaphone, label: "Campaigns" },
    ],
  },
  cleaner: {
    gradient: "from-amber-50 via-rose-50 to-amber-50",
    text: "text-slate-900",
    sub: "text-amber-700",
    bodyText: "text-slate-600",
    chipBg: "bg-white border border-amber-200 text-amber-800",
    accent: "text-amber-700",
    Icon: Sparkles,
    tagline: "Turnover crew",
    capabilities: [
      { icon: CalendarDays, label: "Calendar" },
      { icon: Sparkles, label: "Cleaning board" },
      { icon: Key, label: "Door codes + Wi-Fi" },
      { icon: Clock, label: "07:00 SMS pre-brief" },
    ],
  },
};

const QUALITY_TONE = { green: "bg-emerald-100 text-emerald-800", amber: "bg-amber-100 text-amber-800", red: "bg-rose-100 text-rose-800" };

function QualityChip({ s, testId }) {
  if (!s) return <span className="text-xs text-slate-300" data-testid={testId}>—</span>;
  return (
    <span data-testid={testId} title={`${s.flag_free} of ${s.total} turnovers flag-free · ${s.checked} AI-checked`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${QUALITY_TONE[s.band]}`}>
      {s.pct}% <span className="font-normal opacity-75">· {s.total} turnover{s.total === 1 ? "" : "s"}</span>
    </span>
  );
}

export default function Team() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "admin" });
  const [busy, setBusy] = useState(false);
  const [invited, setInvited] = useState(null);

  const load = () => api.get("/team").then((r) => setRows(r.data));
  const staff = user?.role === "owner" || user?.role === "admin";
  const [quality, setQuality] = useState({});
  useEffect(() => { if (staff) api.get("/team/quality-scores").then((r) => setQuality(r.data.scores)).catch(() => {}); }, [staff]);
  const [guideProgress, setGuideProgress] = useState(null);
  const [certs, setCerts] = useState({});
  const downloadCert = async (u) => {
    const r = await api.get(`/training/certificates/${u.id}/pdf`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    a.download = `certificate-${(u.name || u.email).replace(/\s+/g, "-").toLowerCase()}.pdf`;
    a.click();
  };
  useEffect(() => {
    api.get("/training/onboarding-completion").then(({ data }) => {
      const total = data.guides.length;
      const unread = {};
      data.guides.forEach((g) => g.stragglers.forEach((st) => { unread[st.id] = (unread[st.id] || 0) + 1; }));
      setGuideProgress({ total, unread });
    }).catch(() => {});
    api.get("/training/certificates").then(({ data }) => setCerts(Object.fromEntries(data.map((c) => [c.id, c.training_certificate_sent_at])))).catch(() => {});
  }, []);
  useEffect(() => {
    load();
  }, []);

  if (user && user.role !== "owner") {
    return (
      <Layout title="Team" subtitle="Only the owner can manage team members.">
        <div className="ns-card p-10 text-center">
          <ShieldCheck className="w-8 h-8 text-slate-300 mx-auto" />
          <div className="font-serif text-2xl mt-4">Owner-only area</div>
          <p className="text-sm text-slate-500 mt-2">
            Ask your owner to grant you access if you need to invite team
            members.
          </p>
        </div>
      </Layout>
    );
  }

  const invite = async () => {
    if (!form.email) {
      toast.error("Enter an email address");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/team/invite", form);
      setInvited(data);
      setForm({ email: "", name: "", role: "admin" });
      setOpen(false);
      toast.success(
        data.email_sent
          ? `Invitation sent to ${data.user.email}`
          : `User created — share the temporary password manually`,
      );
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    try {
      await api.patch(`/team/${u.id}/role`, { role });
      toast.success(`${u.name} is now ${role}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email} from the team?`)) return;
    try {
      await api.delete(`/team/${u.id}`);
      toast.success("Team member removed");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const resendWelcome = async (u) => {
    if (!window.confirm(`Send a fresh welcome email with a new temporary password to ${u.email}?`)) return;
    try {
      const { data } = await api.post(`/team/${u.id}/resend-welcome`);
      setInvited({
        user: u,
        temp_password: data.temp_password,
        email_sent: data.email_sent,
      });
      toast.success(
        data.email_sent
          ? `Welcome email re-sent to ${u.email}`
          : `New password set — share manually`,
      );
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const copyTemp = () => {
    if (!invited?.temp_password) return;
    navigator.clipboard.writeText(invited.temp_password);
    toast.success("Temporary password copied");
  };

  return (
    <Layout
      title="Team"
      subtitle="Invite owners and administrators. Each person gets their own login and role."
      actions={
        <Button
          data-testid={TEAM.inviteBtn}
          onClick={() => setOpen(true)}
          className="rounded-full ns-btn-primary h-11 px-6"
        >
          <UserPlus2 className="w-4 h-4 mr-2" /> Invite person
        </Button>
      }
    >
      <div data-testid={TEAM.root} className="space-y-6">
        {invited && (
          <div className="ns-card p-5 bg-teal-50/60 border-teal-100">
            <div className="ns-label">Invitation created</div>
            <div className="mt-2 text-sm">
              <span className="font-medium">{invited.user.email}</span> ·{" "}
              <span className="capitalize">{invited.user.role}</span> ·{" "}
              {invited.email_sent ? (
                <span className="text-teal-700">email sent</span>
              ) : (
                <span className="text-amber-700">
                  email failed — share manually
                </span>
              )}
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-slate-500">
                Temporary password:
              </span>
              <code className="bg-white border border-slate-200 rounded px-2 py-1 text-xs">
                {invited.temp_password}
              </code>
              <button
                onClick={copyTemp}
                className="inline-flex items-center gap-1 text-xs text-teal-700 hover:underline"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
              <button
                onClick={() => setInvited(null)}
                className="ml-auto text-xs text-slate-400 hover:text-slate-600"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <div className="ns-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-left border-b border-slate-100">
                <th className="ns-label px-6 py-4">Name</th>
                <th className="ns-label px-6 py-4">Email</th>
                <th className="ns-label px-6 py-4">Role</th>
                {staff && <th className="ns-label px-6 py-4" title="Share of completed turnovers in the last 90 days with no AI-flagged rooms or confirmed damage">Quality · 90d</th>}
                <th className="ns-label px-6 py-4">Joined</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr
                  key={u.id}
                  data-testid={TEAM.row(u.id)}
                  className="border-b border-slate-100 hover:bg-teal-50/20"
                >
                  <td className="px-6 py-4">
                    <div className="font-medium flex items-center gap-1.5">
                      {u.name}
                      {guideProgress?.total > 0 && ["admin", "owner", "cleaner"].includes(u.role) && (
                        (guideProgress.unread[u.id] || 0) === 0 ? (
                          <span title="Read every training guide" data-testid={`team-guides-complete-${u.id}`}
                            className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500 text-white">
                            <Check className="w-3 h-3" strokeWidth={3} />
                          </span>
                        ) : null
                      )}
                      {certs[u.id] && (
                        <button onClick={() => downloadCert(u)} data-testid={`team-certificate-${u.id}`} title={`Completed ${certs[u.id].slice(0, 10)}`}
                          className="text-[10px] font-normal text-teal-700 hover:text-teal-900 border border-teal-100 rounded-full px-2 py-0.5 transition-colors">
                          Certificate · {certs[u.id].slice(0, 10)}
                        </button>
                      )}
                      {guideProgress?.total > 0 && ["admin", "owner", "cleaner"].includes(u.role) && (guideProgress.unread[u.id] || 0) > 0 && (
                        (
                          <span className="text-[10px] text-slate-400 font-normal" data-testid={`team-guides-progress-${u.id}`}>
                            {guideProgress.total - guideProgress.unread[u.id]}/{guideProgress.total} guides
                          </span>
                        )
                      )}
                    </div>
                    {u.invited_by && (
                      <div className="text-[11px] text-slate-400">
                        invited by {u.invited_by}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {u.email}
                  </td>
                  <td className="px-6 py-4">
                    {u.email === user?.email ? (
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs capitalize ${
                          ROLE_TONE[u.role] || "bg-slate-100"
                        }`}
                      >
                        {u.role} (you)
                      </span>
                    ) : (
                      <Select
                        value={u.role}
                        onValueChange={(v) => changeRole(u, v)}
                      >
                        <SelectTrigger className="h-9 w-36 bg-white capitalize">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="owner">Owner</SelectItem>
                          <SelectItem value="admin">Administrator</SelectItem>
                          <SelectItem value="cleaner">Cleaner</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  {staff && (
                    <td className="px-6 py-4">
                      <QualityChip s={quality[u.id]} testId={`team-quality-${u.id}`} />
                    </td>
                  )}
                  <td className="px-6 py-4 text-xs text-slate-500">
                    {u.created_at
                      ? new Date(u.created_at).toLocaleDateString()
                      : ""}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-1 justify-end">
                      {u.email !== user?.email && (
                        <button
                          data-testid={TEAM.resendWelcome(u.id)}
                          onClick={() => resendWelcome(u)}
                          className="text-[11px] tracking-wider uppercase text-teal-700 hover:underline px-2"
                          title="Send a fresh welcome email with a new temporary password"
                        >
                          Resend welcome
                        </button>
                      )}
                      {u.email !== user?.email && (
                        <button
                          onClick={() => remove(u)}
                          className="text-slate-300 hover:text-red-600 p-1.5"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-slate-400"
                  >
                    Just you so far — invite your first administrator.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="grid md:grid-cols-3 gap-4 max-w-5xl">
          {["owner", "admin", "cleaner"].map((r) => {
            const t = ROLE_THEME[r];
            const Icon = t.Icon;
            return (
              <div
                key={r}
                data-testid={`team-role-card-${r}`}
                className={`relative overflow-hidden rounded-2xl p-6 shadow-sm bg-gradient-to-br ${t.gradient} ${t.text}`}
              >
                <div className="absolute -top-6 -right-6 opacity-10">
                  <Icon className="w-32 h-32" strokeWidth={1.25} />
                </div>
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${t.chipBg}`}>
                      <Icon className={`w-4 h-4 ${t.accent}`} />
                    </span>
                    <div className={`text-[10px] uppercase tracking-[0.24em] ${t.sub}`}>
                      Role · {ROLE_LABEL[r].toLowerCase()}
                    </div>
                  </div>
                  <div className="font-serif text-2xl mt-3 leading-tight">{ROLE_LABEL[r]}</div>
                  <div className={`text-xs mt-0.5 ${t.sub}`}>{t.tagline}</div>
                  <p className={`text-xs mt-3 ${t.bodyText} leading-relaxed`}>{ROLE_DESC[r]}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {t.capabilities.map((cap, i) => {
                      const CapIcon = cap.icon;
                      return (
                        <span
                          key={i}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] ${t.chipBg}`}
                        >
                          <CapIcon className="w-3 h-3" /> {cap.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Invite team member
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="ns-label">Email</Label>
              <Input
                data-testid={TEAM.inviteEmail}
                type="email"
                className="mt-2 h-11 bg-white"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="name@example.com"
              />
            </div>
            <div>
              <Label className="ns-label">Name (optional)</Label>
              <Input
                data-testid={TEAM.inviteName}
                className="mt-2 h-11 bg-white"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label className="ns-label">Role</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
              >
                <SelectTrigger
                  data-testid={TEAM.inviteRole}
                  className="mt-2 h-11 bg-white capitalize"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Owner — full access</SelectItem>
                  <SelectItem value="admin">
                    Administrator — everyday CRM
                  </SelectItem>
                  <SelectItem value="cleaner">
                    Cleaner — turnover crew (read-only)
                  </SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-400 mt-2">
                {ROLE_DESC[form.role]}
              </div>
            </div>
            <div className="text-xs text-slate-500 bg-stone-50 border border-stone-100 rounded-lg p-3">
              An invitation email with a temporary password will be sent from
              Newport Stays. You&apos;ll also see the password here so you can
              share it manually if needed.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid={TEAM.sendInviteBtn}
              disabled={busy}
              onClick={invite}
              className="rounded-full ns-btn-primary px-6"
            >
              {busy ? "Sending…" : "Send invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
